import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Bookmark, LineChart, Loader2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api } from "@/lib/api";

// shared set of instrument ids that are in any of the user's watchlists
let watched = { ids: null, p: null, at: 0, subs: new Set() };
export const refreshWatched = () => {
  watched.p = api.get("/watchlists/ids").then((r) => { watched.ids = new Set(r.data.ids); watched.at = Date.now(); })
    .catch(() => { watched.ids = watched.ids || new Set(); })
    .finally(() => { watched.p = null; watched.subs.forEach((f) => f()); });
  return watched.p;
};
const useWatched = (id) => {
  const [, tick] = useState(0);
  React.useEffect(() => {
    const f = () => tick((n) => n + 1);
    watched.subs.add(f);
    if (!watched.p && (!watched.ids || Date.now() - watched.at > 60000)) refreshWatched();
    return () => watched.subs.delete(f);
  }, []);
  return !!watched.ids?.has(id);
};

function Targets({ instrumentId, symbol, onDone }) {
  const [lists, setLists] = useState(null);
  React.useEffect(() => { api.get("/watchlists/names").then((r) => setLists(r.data.watchlists)).catch(() => setLists([])); }, []);
  const add = async (wl, tab) => {
    try {
      await api.post(`/watchlists/${wl.id}/items`, { instrument_id: instrumentId, tab_id: tab?.id || null });
      toast.success(`${symbol} added to ${wl.name}${tab ? ` › ${tab.name}` : ""} · ${new Date().toLocaleDateString("en-IN")}`);
      refreshWatched();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add — refresh and try again");
    }
    onDone?.();
  };
  if (!lists) return <Loader2 className="h-4 w-4 animate-spin text-blue-600 m-2" />;
  return (
    <div className="max-h-64 overflow-y-auto">
      {lists.map((wl) => (
        <div key={wl.id}>
          <button onClick={() => add(wl)} data-testid={`watch-add-${symbol}-${wl.id}`}
            className="w-full text-left px-2 py-1.5 text-xs font-mono text-slate-900 hover:bg-slate-100 flex items-center gap-1.5">
            <Bookmark className="h-3 w-3 text-blue-600" /> {wl.name}
          </button>
          {(wl.tabs || []).map((t) => (
            <button key={t.id} onClick={() => add(wl, t)} data-testid={`watch-add-${symbol}-${t.id}`}
              className="w-full text-left pl-6 pr-2 py-1 text-[11px] font-mono text-slate-600 hover:bg-slate-100">› {t.name}</button>
          ))}
        </div>
      ))}
    </div>
  );
}

// "+" button for any stock row: pick a watchlist (or a tab inside it)
export const WatchAdd = ({ instrumentId, symbol, className = "" }) => {
  const [open, setOpen] = useState(false);
  const isWatched = useWatched(instrumentId);
  if (!instrumentId) return null;
  return (
    <span className={`shrink-0 inline-flex items-center gap-1 ${className}`}>
    {isWatched && (
      <span title="In your watchlist" data-testid={`watch-mark-${symbol}`}
        className="h-5 px-1 inline-flex items-center gap-0.5 rounded-sm bg-blue-600 text-white text-[9px] font-mono font-bold">
        <Bookmark className="h-2.5 w-2.5 fill-white" />WL
      </span>
    )}
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button onClick={(e) => e.stopPropagation()} title="Add to watchlist" data-testid={`watch-plus-${symbol}`}
          className="shrink-0 h-5 w-5 inline-flex items-center justify-center border border-slate-300 text-slate-500 hover:text-blue-600 hover:border-blue-500">
          <Plus className="h-3 w-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-1 bg-white border-slate-200" onClick={(e) => e.stopPropagation()}>
        <p className="px-2 py-1 text-[10px] font-mono text-slate-500">Add {symbol} to…</p>
        {open && <Targets instrumentId={instrumentId} symbol={symbol} onDone={() => setOpen(false)} />}
      </PopoverContent>
    </Popover>
    </span>
  );
};

// click-menu wrapper: Open chart / Add to watchlist
export const StockMenu = ({ instrumentId, symbol, children }) => {
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const navigate = useNavigate();
  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setAdding(false); }}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent className="w-56 p-1 bg-white border-slate-200" data-testid={`stock-menu-${symbol}`}>
        <p className="px-2 py-1 text-[11px] font-mono font-bold text-slate-900">{symbol}</p>
        {!adding ? (
          <>
            <button onClick={() => navigate(`/app/instrument/${instrumentId}`)} data-testid={`stock-menu-chart-${symbol}`}
              className="w-full text-left px-2 py-1.5 text-xs font-mono text-slate-800 hover:bg-slate-100 flex items-center gap-1.5"><LineChart className="h-3.5 w-3.5 text-blue-600" /> Open chart</button>
            <button onClick={() => setAdding(true)} data-testid={`stock-menu-watch-${symbol}`}
              className="w-full text-left px-2 py-1.5 text-xs font-mono text-slate-800 hover:bg-slate-100 flex items-center gap-1.5"><Bookmark className="h-3.5 w-3.5 text-blue-600" /> Add to watchlist</button>
          </>
        ) : <Targets instrumentId={instrumentId} symbol={symbol} onDone={() => { setOpen(false); setAdding(false); }} />}
      </PopoverContent>
    </Popover>
  );
};
